import { useEffect, useRef, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';
import usePurchase from 'hooks/store/usePurchase';
import { isAdyenApplePayEnabled } from 'utils/adyen';
import adyen from '../../../payments/core/engines/adyen';
import { applePayPaymentAttempt } from '../../actions/applePay';
import { updatePurchase } from '../../actions/purchase';
import { setError } from '../../actions';
import errorMonitoring from '../../errorMonitoring';

/**
 * Apple Pay session timeout in milliseconds.
 * Apple Pay has a 30-second timeout, we use 25 seconds to have a safety margin.
 */
const APPLE_PAY_SESSION_TIMEOUT_MS = 25000;

/**
 * useAdyenApplePay hook.
 * Manages Adyen Apple Pay component lifecycle, configuration, and callbacks.
 *
 * Implements async payment coordination between onSubmit and onAuthorized:
 * - onSubmit: Dispatches payment action and stores a promise for the result
 * - onAuthorized: Waits for payment result with timeout, then resolves/rejects Apple Pay session
 *
 * @param {object} props - onPaymentButtonClick.
 * @param {Function} props.onPaymentButtonClick - Callback when Apple Pay button is clicked.
 * @param {Function} [props.beforeWalletPayment] - Optional current Quick Purchase check before dispatch.
 * @returns {object} Apple Pay component state and references.
 */
function useAdyenApplePay({ onPaymentButtonClick, beforeWalletPayment }) {
  const beforeWalletPaymentRef = useRef(beforeWalletPayment);
  beforeWalletPaymentRef.current = beforeWalletPayment;
  const applePayComponentRef = useRef(null);
  const containerRef = useRef(null);
  const isInitialized = useRef(false);

  // Refs for async payment coordination
  // These store the resolve/reject functions from onAuthorized to be called when payment completes
  const paymentResolveRef = useRef(null);
  const paymentRejectRef = useRef(null);
  const paymentTimeoutRef = useRef(null);
  const paymentResultRef = useRef(null); // 'success' | 'error' | null
  const paymentSubmissionRef = useRef(null);

  // Holds the purchaser-save thunk handed back by Checkout's gate when the purchaser form changed
  // but is valid: the Apple Pay sheet opens immediately within the click gesture and the PUT is run
  // (and awaited) at submit time, since the ApplePaySession cannot be created after async work.
  const savePurchaserRef = useRef(null);

  const [isLoading, setIsLoading] = useState(true);
  const [error, setErrorState] = useState(null);
  const [isAvailable, setIsAvailable] = useState(false);
  const [isConfirmingPayment, setIsConfirmingPayment] = useState(false);

  const dispatch = useDispatch();
  const { t } = useTranslation('payment');
  const { env, copies } = useSelector((state) => state.whitelabelConfig);
  const { currency } = env;
  const {
    provider: { name: WhitelabelName },
  } = copies;
  const { adyen: adyenConfig } = env;

  const { total: purchaseTotal, token: purchaseToken } = usePurchase();

  // Helper function to ensure we always have a valid numeric amount
  const getValidAmount = useCallback((value) => {
    const numValue = Number(value);
    return Number.isFinite(numValue) && numValue > 0 ? numValue : 0;
  }, []);

  // Ref for stable initialization - prevents remounting when total changes
  // Initialize with current purchase total to ensure valid numeric value on first mount
  const purchaseTotalRef = useRef(getValidAmount(purchaseTotal));

  /**
   * Cleans up async payment coordination state.
   * Called after Apple Pay session is completed (success or failure).
   */
  const cleanupPaymentCoordination = useCallback(() => {
    if (paymentTimeoutRef.current) {
      clearTimeout(paymentTimeoutRef.current);
      paymentTimeoutRef.current = null;
    }
    paymentResolveRef.current = null;
    paymentRejectRef.current = null;
    paymentResultRef.current = null;
  }, []);

  /**
   * Completes the Apple Pay session based on payment result.
   * Called either when payment completes or when timeout is reached.
   * @param {boolean} success - Whether the payment was successful.
   */
  const completeApplePaySession = useCallback(
    (success) => {
      if (success && paymentResolveRef.current) {
        console.info('Apple Pay: Completing session with SUCCESS');
        paymentResolveRef.current();
      } else if (!success && paymentRejectRef.current) {
        console.info('Apple Pay: Completing session with FAILURE');
        paymentRejectRef.current();
      }
      cleanupPaymentCoordination();
    },
    [cleanupPaymentCoordination],
  );

  /**
   * Callback for successful payment completion.
   * Called by applePayPaymentAttempt when backend confirms payment.
   */
  const handlePaymentSuccess = useCallback(() => {
    paymentResultRef.current = 'success';

    // Reset confirming state if it was set during timeout
    setIsConfirmingPayment(false);

    // If onAuthorized already stored resolve, complete the session
    if (paymentResolveRef.current) {
      completeApplePaySession(true);
    }
    // Otherwise, onAuthorized will check paymentResultRef when it runs
  }, [completeApplePaySession]);

  /**
   * Callback for payment failure.
   * Called by applePayPaymentAttempt when backend rejects payment.
   */
  const handlePaymentFailure = useCallback(() => {
    paymentResultRef.current = 'error';

    // Reset confirming state if it was set during timeout
    setIsConfirmingPayment(false);

    // If onAuthorized already stored reject, complete the session with failure
    if (paymentRejectRef.current) {
      completeApplePaySession(false);
    }
    // Otherwise, onAuthorized will check paymentResultRef when it runs
  }, [completeApplePaySession]);

  /**
   * Handles internal Apple Pay errors (from try/catch blocks).
   * @param {Object} error - Error object.
   * @param {string} customMessage - Custom error message to show to user.
   * @param {string} sentryInfo - Custom info message for Sentry.
   */
  const handleError = useCallback(
    (error, customMessage, sentryInfo = 'Error on Apple Pay (Adyen)') => {
      // Notify error monitoring service (Sentry)
      errorMonitoring.notify({
        error,
        info: sentryInfo,
      });

      // Update purchase state to stop loading
      dispatch(updatePurchase(false));

      // Show error message to user
      // Using code 301 (payment error), warning style, no redirect (stay on checkout)
      dispatch(
        setError(
          301,
          'error_generating_payment',
          'warning',
          false,
          customMessage || t('apple_pay_error.generic_error'),
        ),
      );
    },
    [dispatch, t],
  );

  /**
   * Handles errors from Adyen Apple Pay component.
   * This is the callback that Adyen calls when an error occurs (e.g., session errors).
   * Adyen v4.x passes only the error object as parameter.
   * @param {Object} adyenError - Error object from Adyen component
   */
  const handleComponentError = useCallback(
    (adyenError) => {
      // Extract error details from Adyen error structure
      const errorMessage = adyenError?.error?.message || adyenError?.message || 'Unknown error';
      const errorType = adyenError?.error?.type || adyenError?.type || 'UNKNOWN';

      console.error(`Apple Pay Adyen Error: [${errorType}] ${errorMessage}`);

      // Ignore user cancellation - not an actual error
      if (
        errorType === 'cancel' ||
        errorType === 'CANCEL' ||
        errorMessage.toLowerCase().includes('cancel')
      ) {
        console.log('Apple Pay: User cancelled payment');
        dispatch(updatePurchase(false));
        return;
      }

      // Notify error monitoring service (Sentry)
      errorMonitoring.notify({
        error: adyenError,
        info: `Apple Pay Error: ${errorType} - ${errorMessage}`,
      });

      // Update purchase state to stop loading
      dispatch(updatePurchase(false));

      // Determine user-friendly error message based on error type/message
      let userMessage;
      if (errorMessage.toLowerCase().includes('session')) {
        userMessage = t('apple_pay_error.session_error');
      } else if (errorMessage.toLowerCase().includes('network')) {
        userMessage = t('apple_pay_error.network_error');
      } else if (errorMessage.toLowerCase().includes('merchant')) {
        userMessage = t('apple_pay_error.merchant_error');
      } else {
        userMessage = t('apple_pay_error.generic_error');
      }

      // Show error message to user
      dispatch(setError(301, 'error_generating_payment', 'warning', false, userMessage));
    },
    [dispatch, t],
  );

  /**
   * Handles Apple Pay button click event.
   * Triggers validation via onPaymentButtonClick callback.
   * Adyen v4.x passes (resolve, reject) as function parameters.
   */
  const handleClick = useCallback(
    (resolve, reject) => {
      // Validate that resolve and reject are functions (Adyen should provide these)
      if (typeof resolve !== 'function' || typeof reject !== 'function') {
        handleError(
          new Error('Invalid Apple Pay onClick parameters'),
          t('apple_pay_error.invalid_parameters'),
          'Adyen Apple Pay onClick received invalid resolve/reject functions',
        );
        return;
      }

      // Reset so a stale save thunk from a previous click never leaks into this attempt.
      savePurchaserRef.current = null;

      // Adyen 4.9 wraps this onClick in a Promise with no rejection handler, so calling its
      // reject() surfaces an "unhandled promise rejection" (Unknown promise rejection reason).
      // The Apple Pay sheet only opens once resolve() runs, so to abort before it opens we
      // simply never resolve. Expose a no-op reject to keep a uniform resolve/reject contract
      // for callers without rejecting Adyen's un-caught Promise.
      const abort = () => {};

      onPaymentButtonClick({
        resolve,
        reject: abort,
        // Lets Checkout defer the purchaser PUT to onSubmit when the form changed but is valid, so
        // resolve() can run synchronously inside this click gesture. Deferring resolve() across the
        // async PUT would lose the gesture and trigger "Must create a new ApplePaySession from a
        // user gesture handler".
        registerPurchaserSave: (savePurchaser) => {
          savePurchaserRef.current = typeof savePurchaser === 'function' ? savePurchaser : null;
        },
      });
    },
    [onPaymentButtonClick, handleError, t],
  );

  /**
   * Handles successful Apple Pay authorization.
   * In Adyen v4.9, onAuthorized is called AFTER onSubmit.
   *
   * This callback receives (resolve, reject, event) from Adyen:
   * - resolve(): Completes Apple Pay session with STATUS_SUCCESS (shows checkmark)
   * - reject(): Completes Apple Pay session with STATUS_FAILURE (shows error)
   * - event: ApplePayPaymentAuthorizedEvent with payment details (billingContact, etc.)
   *
   * Implements async coordination: waits for payment result with timeout.
   * @param {Function} resolve - Function to complete Apple Pay with success.
   * @param {Function} reject - Function to complete Apple Pay with failure.
   * @param {ApplePayJS.ApplePayPaymentAuthorizedEvent} event - Authorization event with payment data.
   */
  const handleAuthorized = useCallback(
    (resolve, reject, event) => {
      console.info('Apple Pay Payment authorized', {
        hasBillingContact: !!event?.payment?.billingContact,
        hasShippingContact: !!event?.payment?.shippingContact,
      });

      // Check if payment already completed (onSubmit callback was faster)
      if (paymentResultRef.current === 'success') {
        console.info('Apple Pay: Payment already succeeded, completing session immediately');
        resolve();
        cleanupPaymentCoordination();
        return;
      }

      if (paymentResultRef.current === 'error') {
        console.info('Apple Pay: Payment already failed, completing session with failure');
        reject();
        cleanupPaymentCoordination();
        return;
      }

      // Store resolve/reject for when payment completes
      paymentResolveRef.current = resolve;
      paymentRejectRef.current = reject;

      const submission = paymentSubmissionRef.current;
      // Set timeout for fallback - if payment takes too long, reject to avoid false success
      // and show confirming state while continuing to poll for the result
      paymentTimeoutRef.current = setTimeout(() => {
        // A pending guard must not dispatch after this authorization has failed.
        if (submission) submission.cancelled = true;
        console.warn(
          'Apple Pay: Payment timeout reached, rejecting session. ' +
            'Continuing to reconcile payment status asynchronously.',
        );
        // Reject to avoid showing false success indicator
        if (paymentRejectRef.current) {
          paymentRejectRef.current();
        }
        // Signal confirming state for UI
        setIsConfirmingPayment(true);
        cleanupPaymentCoordination();
      }, APPLE_PAY_SESSION_TIMEOUT_MS);
    },
    [cleanupPaymentCoordination],
  );

  /**
   * Handles Apple Pay payment submission.
   * In Adyen v4.9, onSubmit is called FIRST (before onAuthorized) after user authorization.
   * This callback receives the formatted payment data from Adyen.
   * state.data contains the paymentMethod with applePayToken ready for backend.
   *
   * Implements async coordination: passes callbacks to payment action for result notification.
   *
   * @param {Object} state - Adyen component state with payment data.
   * @param {boolean} state.isValid - Indicates if the payment data is valid.
   * @param {Object} state.data - Payment data containing paymentMethod.
   * @param {Object} state.data.paymentMethod - Payment method object with type and applePayToken.
   * @param {Object} component - Adyen Apple Pay component instance.
   */
  const handleSubmit = useCallback(
    // eslint-disable-next-line no-unused-vars
    async (state, _component) => {
      try {
        // Each submit retains its own cancellation even if a later session starts.
        const submission = { cancelled: false };
        paymentSubmissionRef.current = submission;
        // Reset payment coordination state for new payment attempt
        paymentResultRef.current = null;

        // Validate that state.isValid is true before processing payment
        if (!state.isValid) {
          console.warn('Apple Pay submit - state.isValid is false, skipping payment attempt');
          handleError(
            new Error('Apple Pay state is not valid'),
            t('apple_pay_error.invalid_state'),
            'Apple Pay onSubmit called with invalid state',
          );
          // Signal failure for async coordination
          handlePaymentFailure();
          return;
        }

        // Persist the purchaser the click gate deferred so the sheet could open synchronously.
        // Must complete before the payment attempt so the backend has the latest purchaser data;
        // onSetPurchaser/updatePurchaseFields already surfaces the error, so just fail the session
        // (onAuthorized rejects on paymentResultRef === 'error') and abort on failure.
        if (savePurchaserRef.current) {
          const savePurchaser = savePurchaserRef.current;
          savePurchaserRef.current = null;
          try {
            await savePurchaser();
          } catch (saveError) {
            handlePaymentFailure();
            return;
          }
        }

        if (beforeWalletPaymentRef.current) {
          try {
            await beforeWalletPaymentRef.current();
          } catch (error) {
            dispatch(updatePurchase(false));
            handlePaymentFailure();
            return;
          }
        }

        if (submission.cancelled) {
          dispatch(updatePurchase(false));
          setIsConfirmingPayment(false);
          return;
        }

        // Note: _component provides access to Adyen component methods like setStatus()
        // Currently unused but available for future enhancements

        // Dispatch Redux action to process payment with callbacks for async coordination
        dispatch(
          applePayPaymentAttempt({
            applePayPayload: state.data,
            purchaseToken,
            // Callbacks for async payment coordination with Apple Pay session
            onPaymentSuccess: handlePaymentSuccess,
            onPaymentFailure: handlePaymentFailure,
          }),
        );
      } catch (error) {
        handleError(
          error,
          t('apple_pay_error.processing_error'),
          'Error processing Apple Pay submission',
        );
        // Signal failure for async coordination
        handlePaymentFailure();
      }
    },
    [dispatch, purchaseToken, handleError, handlePaymentSuccess, handlePaymentFailure, t],
  );

  const handleClickRef = useRef(handleClick);
  const handleAuthorizedRef = useRef(handleAuthorized);
  const handleSubmitRef = useRef(handleSubmit);
  const handleComponentErrorRef = useRef(handleComponentError);

  handleClickRef.current = handleClick;
  handleAuthorizedRef.current = handleAuthorized;
  handleSubmitRef.current = handleSubmit;
  handleComponentErrorRef.current = handleComponentError;

  const stableHandleClick = useCallback((...args) => {
    handleClickRef.current(...args);
  }, []);

  const stableHandleAuthorized = useCallback((...args) => {
    handleAuthorizedRef.current(...args);
  }, []);

  const stableHandleSubmit = useCallback((...args) => {
    handleSubmitRef.current(...args);
  }, []);

  const stableHandleComponentError = useCallback((...args) => {
    handleComponentErrorRef.current(...args);
  }, []);

  /**
   * Initialize Apple Pay component.
   */
  useEffect(() => {
    // Track if component is still mounted to prevent state updates after unmount
    let isMounted = true;

    if (!containerRef.current) return;
    if (isInitialized.current) return;

    // Check if Adyen Apple Pay is enabled in config
    if (!isAdyenApplePayEnabled()) {
      setErrorState('apple_pay_not_enabled');
      setIsLoading(false);
      return;
    }

    // Check if Adyen instance is initialized
    const adyenInstance = adyen.getAdyen();
    if (!adyenInstance) {
      console.error('Adyen instance not initialized. Cannot create Apple Pay component.');
      setErrorState('adyen_not_initialized');
      setIsLoading(false);
      return;
    }

    // Check if Apple Pay configuration exists
    if (!adyenConfig || !adyenConfig.applePay) {
      console.error('Apple Pay configuration missing. Cannot create Apple Pay component.');
      setErrorState('apple_pay_config_missing');
      setIsLoading(false);
      return;
    }

    const { merchantName, merchantIdentifier, buttonType, buttonColor } = adyenConfig.applePay;

    try {
      setIsLoading(true);
      setErrorState(null);

      const componentConfig = {
        amount: {
          // Adyen requires amounts in minor units (cents for most currencies)
          // e.g., $550.00 MXN = 55000 cents
          value: Math.round(getValidAmount(purchaseTotalRef.current) * 100),
          currency,
        },
        countryCode: env.country || 'MX',
        configuration: {
          merchantName: merchantName || WhitelabelName,
          merchantId: merchantIdentifier,
        },
        buttonType: buttonType || 'buy',
        buttonColor: buttonColor || 'black',
        requiredShippingContactFields: [], // Digital product - no shipping required
        requiredBillingContactFields: ['postalAddress', 'name'],
        onClick: stableHandleClick,
        onAuthorized: stableHandleAuthorized,
        onSubmit: stableHandleSubmit,
        onError: stableHandleComponentError,
      };

      const applePayComponent = adyen.createApplePay(componentConfig);

      if (!applePayComponent) {
        setErrorState('apple_pay_not_available');
        setIsLoading(false);
        return;
      }

      // Check Apple Pay availability asynchronously before mounting
      // This verifies the device supports Apple Pay and has valid cards configured
      applePayComponent
        .isAvailable()
        .then(() => {
          // Only update state if component is still mounted
          if (!isMounted) return;

          // Apple Pay is available - mount the component
          if (!containerRef.current) return;

          applePayComponent.mount(containerRef.current);
          applePayComponentRef.current = applePayComponent;
          isInitialized.current = true;
          setIsAvailable(true);
          setIsLoading(false);
        })
        .catch((availabilityError) => {
          // Only update state if component is still mounted
          if (!isMounted) return;

          console.error('Apple Pay not available on this device:', availabilityError);
          setErrorState('apple_pay_not_available');
          setIsLoading(false);
        });
    } catch (err) {
      console.error('Error creating Apple Pay component:', err);
      setErrorState('apple_pay_initialization_error');
      setIsLoading(false);
    }

    return () => {
      isMounted = false;

      // Clean up payment coordination state
      cleanupPaymentCoordination();

      if (applePayComponentRef.current) {
        try {
          applePayComponentRef.current.unmount();
          applePayComponentRef.current = null;
          isInitialized.current = false;
          setIsAvailable(false);
        } catch (err) {
          console.error('Error unmounting Apple Pay component:', err);
        }
      }
    };
    // Stable callbacks read latest handlers from refs; only config changes remount the SDK.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adyenConfig, WhitelabelName, currency, env.country]);

  /**
   * Update Apple Pay component amount when purchase total changes.
   * This is a key difference from PayPal v6 - Adyen requires explicit update.
   */
  useEffect(() => {
    // Keep ref in sync for any code that needs current value
    // Ensure we always store a valid numeric amount
    purchaseTotalRef.current = getValidAmount(purchaseTotal);

    if (!applePayComponentRef.current) return;
    if (!isInitialized.current) return;

    try {
      applePayComponentRef.current.update({
        amount: {
          // Adyen requires amounts in minor units (cents for most currencies)
          // e.g., $15.50 USD = 1550 cents
          value: Math.round(getValidAmount(purchaseTotal) * 100),
          currency,
        },
      });
    } catch (error) {
      console.error('Error updating Apple Pay amount:', error);
    }
  }, [purchaseTotal, currency, getValidAmount]);

  return {
    containerRef,
    applePayComponent: applePayComponentRef.current,
    isInitialized: isInitialized.current,
    isLoading,
    error,
    isAvailable,
    isConfirmingPayment,
  };
}

export default useAdyenApplePay;
